import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Stack,
	Avatar,
	Typography,
} from "@mui/material";
import Message from "./Messages/TextMessage";
import MessageAndButtons from "./Messages/MessageAndButtons";
import Gallery from "./Messages/Gallery";
import DataCollector from "./Messages/DataCollector";
import ContactDataCollector from "./Messages/ContactDataCollector";
import ImageMessage from "./Messages/ImageMessage";
import FileInlinePreview from "./Files/FileInlinePreview";
import WidgetRenderer from "./Messages/widgets/WidgetRenderer";
import { useLiveWidgetEnvelope } from "../hooks/widget/useLiveWidgetEnvelope";
import { useWidgetMsg } from "../hooks/widget/useWidgetMsg";
import TraceBlock from "./Messages/TraceBlock";
import { fallbackTextOf, slotOfType, traceOf } from "./Messages/turnSlots";
import { INTELSWIFT_GRAY_LOGO } from "../constants";

export default function Receiver(props) {
	const { content, agents } = props;
	const { agentIDaasId, type } = content;
	const [animate, setAnimate] = useState(false);
	const scrollRef = useRef(null);
	const { msg } = useWidgetMsg();
	// spec-widget-mcp 05 (WM04): a Tier-1 widget rides its reply as `reply.widget` (history) and is
	// patched in place by `record_update_stream.widget` (live) — under the reply's text, never a bubble.
	const widgetEnvelope = useLiveWidgetEnvelope(props.recordId, content?.widget);

	const getAssistant = (agentIDaasId) => {
		const suffix = msg("conversations.ai_agent", "AI Agent");
		const name = agentIDaasId.substring("ai_assistant_".length) + ` (${suffix})`;
		if (!content?.config?.isSummaryGenerated) {
			return (
				<Stack direction="row" spacing={1}>
					<Avatar className="agent-avatar">{INTELSWIFT_GRAY_LOGO}</Avatar>
					<Typography variant="body1">{name}</Typography>
				</Stack>
			);
		}
	};

	const getAgent = (agentIDaasId) => {
		const agent = agents.find((el) => el.agentIDaasId === agentIDaasId);
		if (agent) {
			const { name } = agent;
			return (
				<Stack direction="row" spacing={1}>
					<Typography variant="body1">{name}</Typography>
				</Stack>
			);
		}
		return "";
	};

	// spec-chat-experience 15: the type is a SLOT decision, not a switch that picks one renderer for the
	// whole turn — every reply gets its trace (slot 1), a text answer (slot 2), a rich result (slot 3), its
	// action row (slot 5); chips stay in Chat, outside the turn.
	const slot = slotOfType(type);
	const resultFor = () => {
		switch (type) {
		case "msg_and_buttons":
			return <MessageAndButtons {...props} />;
		case "gallery":
			return <Gallery {...props} />;
		case "image":
			return <ImageMessage link={content.link} />;
		case "contact_data_collector":
			return (
				<ContactDataCollector
					{...props}
					content={content}
					collectorState={props.contactDataCollector}
					recordId={props.recordId}
				/>
			);
		case "ticket_data_collector":
			return (
				<DataCollector
					{...props}
					content={content}
					collectorState={props.dataCollector}
					recordId={props.recordId}
				/>
			);
		case "connect_data_collector":
		case "data_collector":
			return <DataCollector {...props} />;
		default:
			// "widget": a reply that is only a widget — the envelope below is the whole result.
			return null;
		}
	};
	const legacyResult = slot === "result" ? resultFor() : null;
	const traceProps = traceOf(content, props.traceSummary);
	// D-46: a type nobody renders still says something — `fallbackText`, else its message — never a blank turn.
	const fallback = slot === null && !widgetEnvelope ? fallbackTextOf(content) : "";
	if (slot === null && !widgetEnvelope && !fallback) console.warn(`Unrecognized type ${type} of receiver content.`);

	useEffect(() => {
		if (content) {
			setAnimate(true);
			const timeout = setTimeout(() => setAnimate(false), 500);
			return () => clearTimeout(timeout);
		}
	}, [content]);

	return (
		<>
			{content && (
				<Stack className="receiver-ui" direction="column" spacing={1}>
					<Stack className="receiver-info">
						{agentIDaasId && agentIDaasId !== "assistant" && getAgent(agentIDaasId)}
						{agentIDaasId && agentIDaasId.includes("ai_assistant") && getAssistant(agentIDaasId)}
					</Stack>
					<Box
						className={`receiver-message-container ${animate ? "animate-fade-in-left" : ""} ${type === "data_collector" || type === "gallery" || widgetEnvelope ? "full-width" : "fit-content-width"}`}
					>
						{traceProps && (
							<div data-turn-slot="trace">
								<TraceBlock trace={traceProps.trace} summary={traceProps.summary} steps={traceProps.steps} streaming={traceProps.streaming} />
							</div>
						)}
						{slot === "answer" && <Message {...props} recordId={props.recordId} slotted />}
						{fallback && <div data-turn-slot="answer" className="markdown-message-box">{fallback}</div>}
						{(legacyResult || widgetEnvelope) && (
							<div data-turn-slot="result">
								{legacyResult}
								{widgetEnvelope && (
									<WidgetRenderer
										envelope={widgetEnvelope}
										recordId={props.recordId}
										conversationId={props.conversationId}
										send={props.handleRequest}
									/>
								)}
							</div>
						)}
						{content.files && content.files.length > 0 && (
							<FileInlinePreview files={content.files} />
						)}
						{props.feedbackSlot && <div data-turn-slot="feedback">{props.feedbackSlot}</div>}
						<Box ref={scrollRef} />
					</Box>
				</Stack>
			)}
		</>
	);
}
